import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Box } from "@mui/joy";
import { getAllApplicants, getEmployer, getEmployerPosition, getEmployerPositions } from "../../../apiconfig/api";
import { useMetaTags } from "../../../hooks/useMetaTags";
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll";
import S3_URL from "../../../utils/s3";
import {
  LoadingState,
  ErrorState,
  ContentLayout,
} from "../common/components";
import VideoApplicationModal from "../common/components/VideoApplicationModal";
import { useFileUpload } from "hooks/useFileUpload";

const CompanyListing = () => {
  const { company } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { isProcessing, fileInputRef, handleFileChange, handleRequestGallery } =
    useFileUpload(() => {}, () => {});
  
  // Determine view mode based on route
  const isHiringRoute = location.pathname.startsWith('/hiring/');
  
  // API data states
  const [employer, setEmployer] = useState<any>(null);
  const [employerPositions, setEmployerPositions] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(true);

  // // Create fetch function for infinite scroll
  // const fetchCandidates = useCallback(async (page: number, per_page: number) => {
  //   return await getAllApplicants({
  //     submittedToEmployerPositionIds: employerPositions.map(position => position.id),
  //     page,
  //     per_page
  //   });
  // }, [employerPositions]);

  // // Infinite scroll hook for candidates
  // const {
  //   data: candidates,
  //   loading: candidatesLoading,
  //   error: candidatesError,
  //   hasMore: hasMoreCandidates,
  //   loadMore: loadMoreCandidates,
  //   reset: resetCandidates,
  //   total: totalCandidates
  // } = useInfiniteScroll({
  //   fetchFunction: fetchCandidates,
  //   perPage: 5,
  //   enabled: isHiringRoute && !!employerPositions?.length
  // });

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      if (!company) {
        setError("Missing company information");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch employer data
        const employerResponse = await getEmployer(company);
        setEmployer(employerResponse.data.employer);
        
        // Fetch employer positions with sorting
        const positionsResponse = await getEmployerPositions(employerResponse.data.employer.uuid, {
          sort_by: 'displayNameOverride',
          sort_direction: 'ASC'
        });
        
        // Filter to only show public positions (unless in hiring route)
        const allPositions = positionsResponse.data.employerPositions || [];
        const filteredPositions = isHiringRoute 
          ? allPositions 
          : allPositions.filter((position: any) => position.visibility === 'public');
        
        setEmployerPositions(filteredPositions);

        // Candidates will be loaded via infinite scroll hook when employer is viewing
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load position information. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [company]);

  const handleApplyNow = () => {
    localStorage.removeItem("applicantData");
    setVideoPlaying(false); // Pause the intro video
    setModalOpen(true);
  };

  const updateApplicantData = () => {
    const existingData = localStorage.getItem('applicantData');
    const parsedData = existingData ? JSON.parse(existingData) : {};
    
    const updatedData = {
      ...parsedData,
      submittedToEmployerId: employer.uuid,
      employerName: employer.companyName,
    };

    localStorage.setItem('applicantData', JSON.stringify(updatedData));
  };

  const handleRecordNow = () => {
    setModalOpen(false);
    updateApplicantData();
    navigate('/apply/record');
  };

  const handleUploadFromDevice = () => {
    setModalOpen(false);
    updateApplicantData();
    handleRequestGallery();
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setVideoPlaying(true); // Resume video when modal closes
  };

  const handleNavigateBack = () => {
    navigate(`/apply/${company}`);
  };

  // Generate poster image URL for social sharing
  const getPosterImageUrl = (videoId: string) => {
    return `${S3_URL}processed/${videoId}/frames/${videoId}-.0000000.jpg`;
  };

  // Prepare meta tags configuration
  const metaTagsConfig = employer && employerPositions && !loading ? {
    title: `${employer.companyName} - Geek`,
    description: `${employer.companyName} is hiring!`,
    image: getPosterImageUrl(employer.introVideoUuid || "9624bf23-bc28-4ae4-b7eb-886385354d8f"),
    url: window.location.href,
    type: 'website' as const,
    siteName: 'Geek',
    twitterCard: 'summary_large_image' as const,
    twitterSite: '@geekapp',
    twitterCreator: '@geekapp'
  } : {
    title: 'Geek',
    description: 'Web site created using create-react-app'
  };

  // Update meta tags for social sharing
  useMetaTags(metaTagsConfig);

  // Loading state
  if (loading) {
    return <LoadingState />;
  }
  // Error state
  if (error || !employer || !employerPositions) {
    return (
      <ErrorState 
        error={error} 
        company={company} 
        onNavigateBack={handleNavigateBack} 
      />
    );
  }
  
  return (
    <Box sx={{ 
      minHeight: "100vh", 
      bgcolor: "#f4f4f4",
      display: "flex",
      flexDirection: "column"
    }}>
      <ContentLayout 
        employer={employer}
        employerPositions={employerPositions} 
        onApplyNow={handleApplyNow}
        isEmployerView={isHiringRoute}
        videoPlaying={videoPlaying}
        setVideoPlaying={setVideoPlaying}
        // candidates={isHiringRoute ? candidates : []}
        // candidatesLoading={isHiringRoute ? candidatesLoading : false}
        // candidatesError={isHiringRoute ? candidatesError : undefined}
        // hasMoreCandidates={isHiringRoute ? hasMoreCandidates : false}
        // onLoadMoreCandidates={isHiringRoute ? loadMoreCandidates : undefined}
        // totalCandidates={isHiringRoute ? totalCandidates : 0}
      />
      
      <VideoApplicationModal
        open={modalOpen}
        onClose={handleCloseModal}
        onRecordNow={handleRecordNow}
        onUploadFromDevice={handleUploadFromDevice}
        companyName={employer?.companyName || ''}
        positionName={''}
      />
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="video/*"
        style={{ display: "none" }}
      />
    </Box>
  );
};

export default CompanyListing;